/**
 * WebSocket Service (Socket.IO)
 * Context-bound real-time signal channel. No chat; signals only when to refetch from APIs.
 * No contextToken = NO WebSocket connection.
 */

import { io, Socket } from 'socket.io-client'
import { getBackendUrl } from '@/config/environment'
import { isLocalDevHostname } from '@/utils/cookie-domain.util'
import { UrlGenerator } from '@/utils/url-generator.util'
import { queryClient } from '@/app/queryClient'
import { store } from '@/app/store'
import { addNotification } from '@/store/slices/notification.slice'
import { toast } from '@/hooks/use-toast'
import { isRelaxedAutoLogoutEnabled } from '@/utils/auth-relax-auto-logout.util'
import { logger } from '@/lib/logger'

/** Socket event names – must match backend SOCKET_EVENTS so emit and listener align. */
export const SOCKET_EVENTS = {
  NOTIFICATION_NEW: 'notification:new',
  PLATFORM_MAINTENANCE_ENABLED: 'platform.maintenance.enabled',
  PLATFORM_MAINTENANCE_DISABLED: 'platform.maintenance.disabled',
} as const

const DUPLICATE_CONNECT_WARN = '[SocketService] Refusing duplicate connection; disconnect first.'

function getSocketBaseUrl(): string {
  if (typeof window !== 'undefined' && isLocalDevHostname()) {
    return ''
  }
  
  // For production, use absolute backend URL
  const base = getBackendUrl()
  if (base) return base.replace(/\/+$/, '')
  if (typeof window !== 'undefined') return window.location.origin
  return ''
}

class SocketService {
  private socket: Socket | null = null
  private currentToken: string | null = null

  /**
   * Establish socket with auth. Never send accessToken; never connect without contextToken.
   */
  connect(contextToken: string): void {
    if (!contextToken) return
    if (this.socket?.connected) {
      if (this.currentToken === contextToken) return
      this.disconnect()
    }
    if (this.socket && !this.socket.connected) {
      this.socket.removeAllListeners()
      this.socket = null
    }
    if (this.socket) {
      logger.warn(DUPLICATE_CONNECT_WARN)
      return
    }
    this.currentToken = contextToken

    const url = getSocketBaseUrl()
    if (!url) return

    this.socket = io(url, {
      // Try websocket first; fall back to polling when proxy doesn't support Upgrade (common in deployed)
      transports: ['websocket', 'polling'],
      auth: { token: contextToken },
    })
    this.socket.on('connect', () => {
      this.emitSwitchContextFromToken(contextToken)
      
      // Sync notifications after reconnection (WiFi drop, tab sleep, network switch)
      if (typeof window !== 'undefined') {
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] })
      }
    })

    this.socket.on('connect_error', (err: Error) => {
      logger.error('[SocketService] connect_error:', err.message)
    })

    this.socket.on('menu_update', () => {
      window.dispatchEvent(new Event('contextSidebarInvalidated'))
    })

    this.socket.on('permissions-updated', () => {
      window.dispatchEvent(new Event('permissionsUpdated'))
    })

    this.socket.on('context-invalidated', () => {
      if (isRelaxedAutoLogoutEnabled()) {
        return
      }
      window.location.href = UrlGenerator.getBaseLogoutUrl()
    })

    this.socket.on(SOCKET_EVENTS.PLATFORM_MAINTENANCE_ENABLED, (data: { scope: string; allowedRoles: string[] }) => {
      window.dispatchEvent(new CustomEvent('maintenance:enabled', { detail: data }))
    })

    this.socket.on(SOCKET_EVENTS.PLATFORM_MAINTENANCE_DISABLED, () => {
      window.dispatchEvent(new CustomEvent('maintenance:disabled'))
    })

    this.socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (payload: { id?: string; title?: string; message?: string; data?: Record<string, unknown> }) => {
      const title = payload.title ?? 'Notification'
      const message = payload.message ?? ''

      // Dispatch to Redux immediately (stable, never lost even if components unmount)
      store.dispatch(
        addNotification({
          id: payload.id ?? (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `ws-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`),
          organizationId: null,
          branchId: null,
          userId: '',
          type: 'realtime',
          title,
          message,
          data: payload.data ?? null,
          isRead: false,
          createdAt: new Date().toISOString(),
        })
      )

      // Show toast (direct call, no DOM event needed)
      toast({
        title,
        description: message || undefined,
      })

      // Invalidate query cache for full list sync
      if (typeof window !== 'undefined') {
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] })
      }
    })

    this.socket.on('error', (msg: unknown) => {
      logger.error('[SocketService] error:', typeof msg === 'string' ? msg : JSON.stringify(msg))
      if (typeof msg === 'string' && msg.includes('Unauthorized')) {
        if (isRelaxedAutoLogoutEnabled()) {
          return
        }
        window.location.href = UrlGenerator.getBaseLogoutUrl()
      }
    })

    this.socket.on('disconnect', (reason: string) => {
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        this.currentToken = null
        this.socket = null
      }
    })

    if (import.meta.env.DEV) {
      if (typeof window !== 'undefined') {
        (window as any).socket = this.socket;
        (window as any).socketDebug = this.getDebugApi()
      }
    }
  }

  /**
   * Tell server current context so it can put socket in correct rooms.
   * Backend expects switch_context with context, orgSlug, branchSlug (from token).
   */
  private emitSwitchContextFromToken(contextToken: string): void {
    if (!this.socket?.connected) return
    try {
      const payload = this.decodeContextFromToken(contextToken)
      if (payload) {
        this.socket.emit('switch_context', {
          context: payload.contextType || 'platform',
          orgSlug: payload.organizationSlug,
          branchSlug: payload.branchSlug,
        })
      }
    } catch {
      // ignore decode errors
    }
  }

  private decodeContextFromToken(token: string): { contextType?: string; organizationSlug?: string; branchSlug?: string } | null {
    try {
      const parts = token.split('.')
      if (parts.length < 2) return null
      const raw = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      const decoded = JSON.parse(raw) as { contextType?: string; organizationSlug?: string; branchSlug?: string }
      return decoded
    } catch {
      return null
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners()
      // Only call disconnect() when already connected to avoid browser warning
      // "WebSocket is closed before the connection is established" (e.g. React 18 Strict Mode cleanup).
      if (this.socket.connected) {
        this.socket.disconnect()
      }
      this.socket = null
    }
    this.currentToken = null
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      delete (window as any).socket
      delete (window as any).socketDebug
    }
  }

  /**
   * Used on context/branch switch: disconnect old, connect with new contextToken.
   */
  reinitialize(newContextToken: string): void {
    if (!newContextToken) {
      this.disconnect()
      return
    }
    this.disconnect()
    this.connect(newContextToken)
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  getSocket(): Socket | null {
    return this.socket
  }

  private getDebugApi(): { connect: (token: string) => void; disconnect: () => void; socket: Socket | null } {
    return {
      connect: (token: string) => this.connect(token),
      disconnect: () => this.disconnect(),
      socket: this.socket,
    }
  }
}

export const socketService = new SocketService()
export default socketService
